import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { Navigation as NavigationModule } from 'swiper';
import { SwiperSlide } from 'swiper/react';

import { Box } from '@/components/Box';
import { Typography } from '@/components/Typography';
import { getProductTranslatedName } from '@/lib/i18n';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { getBreakpointSize } from '@/theme';
import type { Watch } from '@/views/MyWatchesView';
import { checkIsEligibleForExtendedWarranty } from '@/views/MyWatchView/components/DesktopPreview';
import { SelectYourWatchModal } from '@/views/ServiceRequestView/components/SelectYourWatchModal/SelectYourWatchModal';

import * as EM from '../../../messages';
import * as S from './WarrantyExtensionWatches.styles';

export interface WarrantyExtensionWatchesProps {
  onNavToSearch: () => void;
  onWarrantyExtend: ({
    id,
    sku,
    sn,
    warrantyExtensionDuration,
  }: {
    id: string;
    sku: string;
    sn: string;
    warrantyExtensionDuration: string;
  }) => Promise<void>;
  processingWatchId?: string;
  watches: Watch[];
}

export const WarrantyExtensionWatches = ({
  onNavToSearch,
  onWarrantyExtend,
  processingWatchId,
  watches,
}: WarrantyExtensionWatchesProps) => {
  const {
    warrantyExtensionCampaignEnable,
    warrantyExtensionCampaignEndDate,
    warrantyExtensionCampaignStartDate,
  } = useConfigProvider();
  const { formatMessage } = useIntl();
  const defaultBreakpoints = {
    [getBreakpointSize('xs')]: {
      slidesPerView: 1,
    },
    [getBreakpointSize('md')]: {
      slidesPerView: 2,
    },
    [getBreakpointSize('lg')]: {
      slidesPerView: 3,
    },
  };
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  return (
    <Box width='100%'>
      <S.StyledSwiper
        breakpoints={defaultBreakpoints}
        centerInsufficientSlides
        modules={[NavigationModule]}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        onBeforeInit={(swiper) => {
          if (typeof swiper.params.navigation !== 'boolean') {
            swiper.params.navigation!.prevEl = prevRef.current;
            swiper.params.navigation!.nextEl = nextRef.current;
          }
        }}
        spaceBetween={16}
      >
        {watches.map((watch) => (
          <SwiperSlide key={watch.id}>
            <SelectYourWatchModal.WatchCard
              ctaText={formatMessage(EM.eventMessages.extendWarranty)}
              disabled={
                !checkIsEligibleForExtendedWarranty(
                  watch.is_extendable,
                  warrantyExtensionCampaignEnable,
                  warrantyExtensionCampaignStartDate,
                  warrantyExtensionCampaignEndDate,
                  watch.start_date,
                )
              }
              endDate={watch.end_date}
              isProcessing={processingWatchId === watch.id}
              name={watch?.name && getProductTranslatedName(watch)}
              onClick={() =>
                onWarrantyExtend({
                  sku: watch.sku,
                  sn: watch.sn,
                  warrantyExtensionDuration:
                    watch?.warrantyExtensionDuration?.values[0]?.plainText ??
                    '',
                  id: watch.id,
                })
              }
              sku={watch.sku}
              src={watch?.thumbnail?.url ?? ''}
              startDate={watch.start_date}
            ></SelectYourWatchModal.WatchCard>
          </SwiperSlide>
        ))}
      </S.StyledSwiper>
      <S.StyledNavigation ref={nextRef} type='next' />
      <S.StyledNavigation ref={prevRef} type='prev' />
      <Typography color='primaryDark' mt={2} textAlign='center'>
        {EM.findYourWatchBySearch(onNavToSearch)}
      </Typography>
    </Box>
  );
};
